import { computed, getCurrentInstance, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// UI-FILTROS-01 (§2.5, D7): o filtro no endereço, num lugar só. Cada tela dá o próprio esquema,
// porque cada uma fala um dialeto de URL (o `agent_id` dos relatórios é o atendente; o `from` da
// busca é o remetente). A composable lê e escreve só as chaves do esquema: o resto da URL (`tab`,
// `comparar`, `campanha`, `page`…) é de outra parte da tela e passa intacto.
//
//   schema = { campo: { param, type: 'ids'|'strings'|'id'|'int'|'bool'|'text', default }
//            | { param, read(query), write(valor) } }   // codec próprio (o período)
//
// Um codec próprio pode ter vários parâmetros (`param: ['range', 'from', 'to']`); `write` devolve
// o pedaço da query dele.
//
// Nem o padrão nem o vazio vão para a URL, e ler de volta um campo esvaziado dá o padrão. Por
// isso o `default` de `ids`, `strings`, `text`, `id` e `int` é vazio, salvo num campo que nunca
// fica vazio (a ordenação, com `clearable=false`): um padrão `['open']` não se zera pelo link. O
// `bool` escreve `true` e `false`, e não tem esse limite.
//
// `update` e `reset` devolvem a promessa do `router.replace`. Escritas da composable no mesmo tick
// se somam, mas a navegação de fora ela não enxerga: quem troca a aba (ou outra chave de fora)
// com o próprio `router.replace` espera essa promessa antes de chamar `update` ou `reset`, e quem
// navega depois de um `update` espera a dele. Sem isso, a navegação que sai por último parte da
// rota de antes e desfaz a outra.

const primeiro = bruto => (Array.isArray(bruto) ? bruto[0] : bruto);
const lista = bruto =>
  [bruto]
    .flat()
    .join(',')
    .split(',')
    .map(parte => parte.trim())
    .filter(Boolean);
const inteiro = bruto => {
  const texto = String(bruto ?? '').trim();
  const numero = Number(texto);
  return texto && Number.isInteger(numero) ? numero : undefined;
};
const juntar = valores =>
  Array.isArray(valores) && valores.length ? valores.join(',') : undefined;
const numeroEscrito = valor =>
  Number.isInteger(valor) ? String(valor) : undefined;
// Um Map, e não um objeto: `testes=constructor` num objeto acharia a função do protótipo.
const SIM_OU_NAO = new Map([
  ['true', true],
  ['1', true],
  ['false', false],
  ['0', false],
]);

const TIPOS = {
  // Lista de ids: um link editado à mão perde o que não é inteiro, em vez de ir ao servidor. Um
  // valor só, de um link antigo (`inbox_id=5`), vira [5].
  ids: {
    ler: bruto => lista(bruto).map(Number).filter(Number.isInteger),
    escrever: juntar,
  },
  strings: { ler: lista, escrever: juntar },
  id: { ler: bruto => inteiro(lista(bruto)[0]), escrever: numeroEscrito },
  int: { ler: bruto => inteiro(primeiro(bruto)), escrever: numeroEscrito },
  bool: {
    ler: bruto => SIM_OU_NAO.get(String(primeiro(bruto))),
    escrever: valor => (typeof valor === 'boolean' ? String(valor) : undefined),
  },
  text: {
    ler: bruto => String(primeiro(bruto) ?? '') || undefined,
    escrever: valor => (valor ? String(valor) : undefined),
  },
};

const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const copia = valor =>
  valor === undefined ? undefined : JSON.parse(JSON.stringify(valor));
const semVazios = objeto =>
  Object.fromEntries(
    Object.entries(objeto || {}).filter(([, valor]) => valor !== undefined)
  );

export const useFilterQuery = (schema, { legacy, remember } = {}) => {
  const campos = Object.entries(schema);
  const parametrosDe = (chave, campo) => [campo.param ?? chave].flat();
  const doEsquema = campos.flatMap(([chave, campo]) =>
    parametrosDe(chave, campo)
  );

  // `legacy` traduz parâmetro antigo: `{ antigo: 'novo' }`, ou uma função da query. A função
  // devolve só o que muda, e o resto da query passa intacto: devolver só `{ range }` não pode
  // apagar o `tab` do link. Renomear parâmetro, com o antigo saindo da URL, é a forma objeto. Na
  // função, o antigo só sai se ela o devolver `undefined` (`{ inbox: q.inbox_id, inbox_id:
  // undefined }`): se ficasse no link, seria traduzido de novo a cada leitura, por cima da escolha
  // nova.
  const traduzir = (query = {}) => {
    if (!legacy) return { ...query };
    if (typeof legacy === 'function')
      return semVazios({ ...query, ...legacy({ ...query }) });
    const traduzida = { ...query };
    Object.entries(legacy).forEach(([antigo, novo]) => {
      if (!(antigo in traduzida)) return;
      if (traduzida[novo] === undefined) traduzida[novo] = traduzida[antigo];
      delete traduzida[antigo];
    });
    return traduzida;
  };

  // undefined quando o parâmetro não veio ou não vale nada. O vazio (`?status=`) conta como não
  // veio: não vence o lembrado nem zera o padrão, como no filtrosNaUrl de antes (UF-11).
  const lerDoLink = (chave, campo, query) => {
    if (campo.read) return campo.read(query);
    const bruto = query[parametrosDe(chave, campo)[0]];
    if (bruto === undefined || bruto === null || bruto === '') return undefined;
    return TIPOS[campo.type].ler(bruto);
  };

  const fromQuery = (query = {}) => {
    const traduzida = traduzir(query);
    const filtros = {};
    campos.forEach(([chave, campo]) => {
      const valor = lerDoLink(chave, campo, traduzida) ?? copia(campo.default);
      if (valor !== undefined) filtros[chave] = valor;
    });
    return filtros;
  };

  // O padrão não vai para a URL: o link fica curto, e ler de volta dá o mesmo.
  const toQuery = (filtros = {}) => {
    const query = {};
    campos.forEach(([chave, campo]) => {
      const valor = filtros[chave];
      if (campo.write) {
        Object.assign(query, semVazios(campo.write(valor)));
        return;
      }
      if (valor === undefined || igual(valor, campo.default)) return;
      const escrito = TIPOS[campo.type].escrever(valor);
      if (escrito !== undefined) query[parametrosDe(chave, campo)[0]] = escrito;
    });
    return query;
  };

  // Tem filtro quem traz algum parâmetro do esquema que se leia; `utm_source` não conta.
  const hasAny = (query = {}) => {
    const traduzida = traduzir(query);
    return campos.some(
      ([chave, campo]) =>
        parametrosDe(chave, campo).some(param => param in traduzida) &&
        lerDoLink(chave, campo, traduzida) !== undefined
    );
  };

  // `remember`: os filtros lembrados neste navegador (o Kanban lembra por pipeline). Guarda a
  // query, e o localStorage pode faltar ou recusar (aba anônima, cota): aí só não lembra.
  const chaveLembrada = () =>
    typeof remember?.key === 'function' ? remember.key() : remember?.key;
  // O localStorage não é reativo. Cada `store` sobe a versão, e `filters` relê o lembrado mesmo
  // quando a URL não muda: o "Limpar" de quem abriu pelo lembrado, com a URL já limpa.
  const versaoLembrada = ref(0);
  const recall = () => {
    try {
      const salvo = chaveLembrada()
        ? window.localStorage.getItem(chaveLembrada())
        : null;
      return fromQuery(salvo ? JSON.parse(salvo) : {});
    } catch (erro) {
      return fromQuery({});
    }
  };
  const store = filtros => {
    try {
      if (!chaveLembrada()) return;
      window.localStorage.setItem(
        chaveLembrada(),
        JSON.stringify(toQuery(filtros))
      );
    } catch (erro) {
      // Sem armazenamento, só não lembra.
    }
    versaoLembrada.value += 1;
  };

  // Fora de um componente (um helper que só quer toQuery e fromQuery), não há rota.
  const dentroDeComponente = Boolean(getCurrentInstance());
  const route = dentroDeComponente ? useRoute() : null;
  const router = dentroDeComponente ? useRouter() : null;

  // A URL vence o lembrado: quem abre um link quer ver o que o link diz.
  const filtrosDe = query => {
    if (!remember || hasAny(query)) return fromQuery(query);
    // eslint-disable-next-line no-unused-expressions
    versaoLembrada.value;
    return recall();
  };
  const filters = computed(() => filtrosDe(route?.query || {}));

  // O que não é do esquema, nem parâmetro antigo que ele traduziu, fica como está.
  const foraDoEsquema = query => {
    const traduzida = traduzir(query);
    return Object.fromEntries(
      Object.entries(query).filter(
        ([param]) => !doEsquema.includes(param) && param in traduzida
      )
    );
  };

  // O alvo da escrita que ainda não terminou. A seguinte parte dele, e não do `route.query`, que
  // só muda quando o `replace` termina: dois `update` no mesmo tick somam, e o segundo não desfaz
  // o primeiro. Só a última escrita o zera.
  let pendente = null;
  const base = () => pendente ?? route?.query ?? {};

  const escrever = proximos => {
    if (remember) store(proximos);
    if (!router) return undefined;
    const alvo = { ...foraDoEsquema(base()), ...toQuery(proximos) };
    pendente = alvo;
    return router
      .replace({ path: route.path, hash: route.hash, query: alvo })
      .finally(() => {
        if (pendente === alvo) pendente = null;
      });
  };

  const update = patch => escrever({ ...filtrosDe(base()), ...patch });
  const reset = () => escrever(fromQuery({}));

  return {
    filters,
    update,
    reset,
    toQuery,
    fromQuery,
    hasAny,
    recall,
    store,
  };
};
